import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { toast } from 'react-toastify';
import {
  AllocationWithAmount,
  CheckPaymentDetails,
} from '../payment-method-selection-dialog';
import { generateSerialChecks } from '../utils/generate-check-series';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

type SeriesDraft = {
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
  startDueDate: Date | null;
  amountPerCheck: number | null;
  referenceIdStart: string;
  numberOfChecks: number | null;
};

const defaultDraft: SeriesDraft = {
  bankNumber: '',
  branchNumber: '',
  accountNumber: '',
  startDueDate: null,
  amountPerCheck: null,
  referenceIdStart: '',
  numberOfChecks: null,
};

export interface AddCheckSeriesDialogProps {
  visible: boolean;
  onHide: () => void;
  allocationAmounts: AllocationWithAmount[];
  existingChecks: CheckPaymentDetails[];
  initialBank?: {
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
  };
  onSubmit: (checks: CheckPaymentDetails[]) => void;
}

export const AddCheckSeriesDialog: React.FC<AddCheckSeriesDialogProps> = ({
  visible,
  onHide,
  allocationAmounts,
  existingChecks,
  initialBank,
  onSubmit,
}) => {
  const [draft, setDraft] = useState<SeriesDraft>(defaultDraft);

  useEffect(() => {
    if (!visible) return;
    setDraft({
      ...defaultDraft,
      bankNumber: initialBank?.bankNumber ?? '',
      branchNumber: initialBank?.branchNumber ?? '',
      accountNumber: initialBank?.accountNumber ?? '',
    });
  }, [
    visible,
    initialBank?.bankNumber,
    initialBank?.branchNumber,
    initialBank?.accountNumber,
  ]);

  const remainingInfo = useMemo(() => {
    const totalPayment = round2(
      allocationAmounts.reduce((s, a) => s + (a.amount || 0), 0),
    );
    const alreadyAssigned = round2(
      existingChecks.reduce((s, c) => s + (c.amount || 0), 0),
    );
    const remaining = round2(totalPayment - alreadyAssigned);
    return { totalPayment, alreadyAssigned, remaining };
  }, [allocationAmounts, existingChecks]);

  const canSubmit = useMemo(() => {
    const b = (draft.bankNumber ?? '').trim();
    const br = (draft.branchNumber ?? '').trim();
    const ac = (draft.accountNumber ?? '').trim();
    const ref = (draft.referenceIdStart ?? '').trim();
    const n = draft.numberOfChecks;
    const amt = draft.amountPerCheck;
    return Boolean(
      b &&
        br &&
        ac &&
        ref &&
        draft.startDueDate &&
        typeof n === 'number' &&
        n >= 1 &&
        n <= 60 &&
        typeof amt === 'number' &&
        amt > 0,
    );
  }, [draft]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const b = (draft.bankNumber ?? '').trim();
    const br = (draft.branchNumber ?? '').trim();
    const ac = (draft.accountNumber ?? '').trim();
    const ref = (draft.referenceIdStart ?? '').trim();
    const n = draft.numberOfChecks!;
    const amt = round2(draft.amountPerCheck!);
    const start = draft.startDueDate!;

    if (remainingInfo.remaining <= 0) {
      toast.warn('אין יתרה לחלוקה — כל סכום התשלום כבר מחולק להמחאות');
      return;
    }

    const seriesTotal = round2(n * amt);
    if (seriesTotal !== remainingInfo.remaining) {
      toast.warn(
        `סכום הסדרה (${n} × ₪${amt.toLocaleString('he-IL')} = ₪${seriesTotal.toLocaleString('he-IL')}) חייב להיות זהה ליתרה ₪${remainingInfo.remaining.toLocaleString('he-IL')} (סה״כ תשלום ₪${remainingInfo.totalPayment.toLocaleString('he-IL')} פחות ₪${remainingInfo.alreadyAssigned.toLocaleString('he-IL')} כבר בהמחאות)`,
      );
      return;
    }

    try {
      const checks = generateSerialChecks({
        allocationAmounts,
        existingChecks,
        bankNumber: b,
        branchNumber: br,
        accountNumber: ac,
        startDueDate: start,
        amountPerCheck: amt,
        referenceIdStart: ref,
        numberOfChecks: n,
      });
      onSubmit(checks);
      onHide();
    } catch {
      toast.error('לא ניתן ליצור את הסדרה — וודא שהפרטים תקינים');
    }
  };

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
              color: '#9d174d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden
          >
            <i className="pi pi-list" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: '16px',
                color: '#0f172a',
              }}
            >
              סדרת המחאות
            </div>
            <div
              style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}
            >
              אותם פרטי בנק, מספר המחאה עולה ב־1, תאריך פירעון חודש אחר חודש.
              הפיצול לשירותים נושק על בסיס תור (bucket).
            </div>
          </div>
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '780px', maxWidth: '96vw' }}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
        >
          <Button
            type="button"
            label="ביטול"
            className="p-button-text"
            onClick={onHide}
          />
          <Button
            type="button"
            label="צור סדרה"
            icon="pi pi-check"
            className="p-button-raised"
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
        </div>
      }
    >
      <div
        style={{
          marginBottom: '12px',
          padding: '10px 12px',
          borderRadius: '10px',
          border: '1px solid #bae6fd',
          background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)',
          fontSize: '12px',
          color: '#075985',
          lineHeight: 1.55,
        }}
      >
        יתרה לחלוקה:{' '}
        <strong>₪{remainingInfo.remaining.toLocaleString('he-IL')}</strong> (
        סה״כ ₪{remainingInfo.totalPayment.toLocaleString('he-IL')} − כבר בהמחאות
        ₪{remainingInfo.alreadyAssigned.toLocaleString('he-IL')}). חובה:{' '}
        <strong>מספר המחאות × סכום להמחאה = יתרה</strong>.
      </div>

      <div
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '16px',
          boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Row 1 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                בנק <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.bankNumber}
                onChange={(e) =>
                  setDraft({ ...draft, bankNumber: e.target.value })
                }
                placeholder="מספר בנק"
                style={{ borderRadius: '10px', width: '100%' }}
                className={classNames({
                  'p-invalid': visible && !(draft.bankNumber ?? '').trim(),
                })}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                סניף <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.branchNumber}
                onChange={(e) =>
                  setDraft({ ...draft, branchNumber: e.target.value })
                }
                placeholder="מספר סניף"
                style={{ borderRadius: '10px', width: '100%' }}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                חשבון <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.accountNumber}
                onChange={(e) =>
                  setDraft({ ...draft, accountNumber: e.target.value })
                }
                placeholder="מספר חשבון"
                style={{ borderRadius: '10px', width: '100%' }}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                תאריך פירעון ראשון <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <Calendar
                style={{ direction: 'ltr', width: '100%' }}
                value={draft.startDueDate}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    startDueDate: (e.value as Date) || null,
                  })
                }
                inputStyle={{ direction: 'rtl', width: '100%' }}
                showIcon
                iconPos="right"
                dateFormat="dd/mm/yy"
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                סכום לכל המחאה <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputNumber
                value={draft.amountPerCheck}
                onChange={(e) =>
                  setDraft({ ...draft, amountPerCheck: e.value ?? null })
                }
                prefix="₪"
                inputStyle={{ width: '100%', direction: 'rtl' }}
                style={{ width: '100%' }}
                min={0}
                maxFractionDigits={2}
                locale="he-IL"
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                מספר המחאה ראשון <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.referenceIdStart}
                onChange={(e) =>
                  setDraft({ ...draft, referenceIdStart: e.target.value })
                }
                placeholder="למשל 22333474"
                style={{ borderRadius: '10px', width: '100%' }}
              />
            </div>
          </div>

          {/* Row 3 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: 1,
                maxWidth: '220px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                מספר המחאות בסדרה <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputNumber
                value={draft.numberOfChecks}
                onChange={(e) =>
                  setDraft({ ...draft, numberOfChecks: e.value ?? null })
                }
                inputStyle={{ width: '100%', textAlign: 'right' }}
                style={{ width: '100%', direction: 'ltr' }}
                min={1}
                max={60}
                showButtons
                useGrouping={false}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
